import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { BookMarked, Plus, Save, Trash2, Loader2, X, Check } from "lucide-react";
import {
  listCitationSets,
  saveCitationSet,
  deleteCitationSet,
  type CitationSet,
} from "@/lib/api/citation-sets.functions";

type Props = {
  value: string | null;
  onChange: (id: string | null, set: CitationSet | null) => void;
};

export function CitationSetPicker({ value, onChange }: Props) {
  const list = useServerFn(listCitationSets);
  const save = useServerFn(saveCitationSet);
  const del = useServerFn(deleteCitationSet);

  const [items, setItems] = useState<CitationSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [managing, setManaging] = useState(false);
  const [editing, setEditing] = useState<CitationSet | null>(null);
  const [form, setForm] = useState({
    name: "",
    style: "OSCOLA",
    rules: "",
    bibliography: "",
    is_default: false,
  });
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await list();
      setItems(r.items);
      // Auto-select default on first load if nothing chosen.
      if (!value) {
        const def = r.items.find((i) => i.is_default);
        if (def) onChange(def.id, def);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function startNew() {
    setEditing(null);
    setForm({ name: "", style: "OSCOLA", rules: "", bibliography: "", is_default: false });
    setManaging(true);
  }
  function startEdit(s: CitationSet) {
    setEditing(s);
    setForm({
      name: s.name,
      style: s.style,
      rules: s.rules,
      bibliography: s.bibliography,
      is_default: s.is_default,
    });
    setManaging(true);
  }
  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await save({ data: { ...form, id: editing?.id } });
      setManaging(false);
      await refresh();
      onChange(r.item.id, r.item);
    } finally {
      setBusy(false);
    }
  }
  async function onDelete(id: string) {
    if (!confirm("Delete this saved citation set?")) return;
    setBusy(true);
    try {
      await del({ data: { id } });
      if (value === id) onChange(null, null);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-md border border-input bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <BookMarked className="h-4 w-4 text-navy" />
        <label className="text-sm font-semibold text-navy">Citation set</label>
        <select
          value={value ?? ""}
          onChange={(e) => {
            const id = e.target.value || null;
            const set = id ? items.find((i) => i.id === id) ?? null : null;
            onChange(id, set);
          }}
          className="flex-1 min-w-[180px] rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          disabled={loading}
        >
          <option value="">— None (use default OSCOLA rules only) —</option>
          {items.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} · {s.style}
              {s.is_default ? " ★" : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={startNew}
          className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-semibold hover:bg-muted"
        >
          <Plus className="h-3.5 w-3.5" /> New
        </button>
        {value && (
          <>
            <button
              type="button"
              onClick={() => {
                const s = items.find((i) => i.id === value);
                if (s) startEdit(s);
              }}
              className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs hover:bg-muted"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => value && onDelete(value)}
              className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs text-rose-700 hover:bg-rose-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        Saved sets add your own rules and a persistent bibliography on top of OSCOLA. The Citation Check
        section verifies every entry you cite matches the saved form.
      </p>

      {managing && (
        <div className="mt-3 rounded-md border border-input bg-background p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-navy">
              {editing ? "Edit citation set" : "New citation set"}
            </p>
            <button
              type="button"
              onClick={() => setManaging(false)}
              className="rounded p-1 hover:bg-muted"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={onSave} className="space-y-2">
            <div className="grid gap-2 md:grid-cols-3">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-navy">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  minLength={2}
                  maxLength={120}
                  placeholder="e.g. Labour Law LLB Y3 — OSCOLA + house rules"
                  className="mt-0.5 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-navy">Style</label>
                <input
                  value={form.style}
                  onChange={(e) => setForm({ ...form, style: e.target.value })}
                  maxLength={60}
                  className="mt-0.5 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">
                Extra rules (applied on top of OSCOLA)
              </label>
              <textarea
                value={form.rules}
                onChange={(e) => setForm({ ...form, rules: e.target.value })}
                rows={4}
                maxLength={8000}
                placeholder={"e.g.\n- Always cite the Constitution first when a right is engaged.\n- Prefer the CC/SCA judgment over the HC decision.\n- Include parallel neutral + SA report citations."}
                className="mt-0.5 w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-navy">
                Preferred bibliography entries (one per line — merged into every output)
              </label>
              <textarea
                value={form.bibliography}
                onChange={(e) => setForm({ ...form, bibliography: e.target.value })}
                rows={6}
                maxLength={20000}
                placeholder={"e.g.\n*S v Makwanyane* 1995 (3) SA 391 (CC)\nConstitution of the Republic of South Africa, 1996\nJ Neethling, *Law of Delict* (8th edn, LexisNexis 2020)"}
                className="mt-0.5 w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs font-mono"
              />
            </div>
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.is_default}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              />
              Use as my default set (auto-selected on every research tool)
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setManaging(false)}
                className="rounded-md border border-input px-3 py-1.5 text-xs hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                {editing ? "Save changes" : "Save set"}
              </button>
            </div>
            {editing && form.is_default && (
              <p className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
                <Check className="h-3 w-3" /> Marked as default
              </p>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
